import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import Avatar from "../components/common/Avatar";
import api from "../services/api";
import { getAssetUrl } from "../services/helper";
import { useNavigate } from "react-router-dom";
import { MessageSquare } from "lucide-react";

const SuggestedContacts = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [allContacts, setAllContacts] = useState([]);

  const { data: pageData, isLoading, error, isFetching } = useQuery({
    queryKey: ["connected-contacts", page],
    queryFn: () => api.fetchConnectedContact("",page,10),
    keepPreviousData: true,
  });

  const newContacts = pageData?.data || [];
  const pagination = pageData?.pagination || {};
  const hasMore = pagination.hasNext || false;

  // Append new contacts (Load More)
  useEffect(() => {
    if (newContacts.length > 0) {
      setAllContacts((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const filteredNew = newContacts.filter((item) => !existingIds.has(item.id));
        return [...prev, ...filteredNew];
      });
    }
  }, [newContacts]);

  const loadMore = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-56px)] w-full overflow-y-auto 
                      [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden 
                      py-6 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[28px] sm:text-[32px] font-bold text-[#0F172A] font-body">
             Contacts
          </h1>
          {/* <p className="text-gray-500 mt-1">People you might want to connect with</p> */}
        </div>

        {/* Loading State */}
        {isLoading && allContacts.length === 0 ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#1F3A5F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20 text-red-500">Failed to load contacts. Please try again.</div>
        ) : allContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-4xl">
              👥
            </div>
            <h3 className="text-2xl font-semibold text-[#0F172A]">No Suggested Contacts</h3>
            <p className="text-gray-500 mt-2">We'll show you people you might know soon.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allContacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-100 hover:border-gray-200 transition-all"
              >
                {/* Avatar + Info */}
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1 min-w-0"
                  onClick={() => navigate(`/other-profile/${contact.id}`)}
                >
                  <Avatar
                    src={getAssetUrl(contact.avatar)}
                    alt={contact.name}
                    size={58}
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[17px] font-semibold text-[#0F172A] truncate">
                      {contact.name}
                    </p>
                    <p className="text-sm text-gray-500 capitalize">suggested</p>
                  </div>
                </div>

                {/* Message Button */}
                <button
                  onClick={() => navigate(`/messages?chat_id=${contact.id}`)}
                  className="w-11 h-11 rounded-full bg-[#1F314733] flex items-center justify-center hover:bg-[#1F3A60] hover:text-white transition-all flex-shrink-0"
                >
                  <MessageSquare size={20} className="text-[#1F3A60]" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="flex justify-center mt-10 mb-12">
            <button
              onClick={loadMore}
              disabled={isFetching}
              className="px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors disabled:opacity-70"
            >
              {isFetching ? "Loading more..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuggestedContacts;